import { attr, hasMany } from '@ember-data/model';
import { service } from '@ember/service';
import { Type } from '@warp-drive/core-types/symbols';
import moment from 'moment-timezone';
import BaseModel from 'volta/models/base-model';
import { collectionCmd, resourceCommand } from 'volta/utils/api';
import { indexByFn } from 'volta/utils/array-utils';
import { FrequencyOptions } from 'volta/utils/date-utils';

import type { IResourceName } from 'volta/models/base-model';
import type { IAggTasksViewMap } from 'volta/models/types/tasks';
import type UserProfile from 'volta/models/user-profile';
import type SessionUserService from 'volta/services/session-user';
import type { IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import type { TForecastFrequency, TFrequencyOption } from 'volta/utils/date-utils';
import type { THueVariationString } from 'volta/utils/style-utils';
import type { TExternalForecastField, TForecastGroupField } from './external-forecast';

export const ForecastProjectResourceName: IResourceName = {
  singular: 'forecastProject',
  plural: 'forecastProjects'
};

export default class ForecastProject extends BaseModel {
  @service declare sessionUser: SessionUserService;

  // Commands

  static CreateForecastProject = collectionCmd<
    ICreateForecastProjectCmd,
    ForecastProject,
    typeof ForecastProject
  >('CreateForecastProject');

  UpdateForecastProject = resourceCommand<IUpdateForecastProjectCmd, ForecastProject>(
    'UpdateForecastProject'
  );

  ValidateForecastProject = resourceCommand<{}, ForecastProject>('ValidateForecastProject');

  RefreshForecastProjectSelection = resourceCommand<
    { skuIds?: string[]; sources?: string[] },
    ForecastProject
  >('RefreshForecastProjectSelection');

  CreateForecastProjectRevision = resourceCommand<{ revision: string }, ForecastProject>(
    'CreateForecastProjectRevision'
  );

  DeleteForecastProject = resourceCommand<{}, ForecastProject>('DeleteForecastProject');

  // Attributes

  @attr('string')
  declare name: string;

  @attr('string')
  declare fullName: string;

  @attr('string')
  declare revision?: string;

  @attr('number', { defaultValue: 0 })
  declare revisionNumber: number;

  @attr('string')
  description?: string;

  @attr('boolean', { defaultValue: false })
  declare automlEnabled: boolean;

  @attr({ defaultValue: () => ({}) })
  declare skuSelection: IJsonApiQuery['filter'];

  @attr('string', { defaultValue: 'W' })
  declare forecastFrequency: TForecastFrequency;

  @attr('number')
  declare forecastHorizon: number;

  @attr('array')
  selectedForecastQuantiles?: TForecastQuantile[];

  @attr('array')
  selectedManualSources?: string[];

  @attr('array')
  manualForecastTypes?: string[];

  @attr('array')
  defaultGroupBy?: TExternalForecastField[][];

  @attr('string')
  declare createdBy: string;

  @attr('string')
  updatedBy?: string;

  @attr('string')
  deactivatedBy?: string;

  @attr('string')
  validatedBy?: string;

  @hasMany<UserProfile>('user-profile', { async: false, inverse: null })
  declare contributors: UserProfile[];

  @attr({ defaultValue: () => [] })
  declare permissions: Record<string, TForecastEntityPermission[]>;

  @attr('array', { defaultValue: () => [] })
  declare contributorIds: string[];

  @attr('string')
  declare status: TForecastProjectStatus;

  @attr('number', { defaultValue: 0 })
  declare validationCompletion: number;

  /**
   * Aggregated data about each task type
   */
  @attr({ defaultValue: () => ({}) })
  declare tasks: IAggTasksViewMap;

  @attr({ defaultValue: () => ({ skuCount: 0, sourceCount: 0, excludedSkuCount: 0 }) })
  declare fpStats: TForecastProjectStats;

  // AWS Forecast specific fields

  @attr('string')
  scheduleId?: string;

  @attr({ defaultValue: () => [] })
  declare metadataModel: TSkuProperty[];

  @attr({ defaultValue: () => [] })
  declare rtsModel: TRTSProperty[];

  @attr('string')
  tsDatasetArn?: string;

  @attr('string')
  rtsDatasetArn?: string;

  @attr('string')
  metadataDatasetArn?: string;

  @attr('string')
  dataGroupArn?: string;

  @attr('date')
  deactivatedAt?: Date;

  @attr('date')
  validatedAt?: Date;

  // Getters

  get isTemplate() {
    return this.revisionNumber === 0;
  }

  get isReadyForReview() {
    return this.status === 'READY_FOR_REVIEW';
  }

  get isValidated() {
    return this.status === 'VALIDATED';
  }

  get horizonAsDuration() {
    return moment.duration(this.forecastHorizon, this.forecastFrequencyOption.period).toString();
  }

  get contributorsRegistry() {
    return this.contributors.reduce(
      (acc, c) => {
        if (c.id) {
          acc[c.id] = c;
        }
        return acc;
      },
      {} as Record<string, UserProfile>
    );
  }

  get statusOption() {
    return ForecastProjectStatusColors[this.status];
  }

  get forecastFrequencyOption() {
    return ForecastFrequencyOptions[this.forecastFrequency];
  }

  get filtersCount() {
    return Object.keys(this.skuSelection ?? {}).length;
  }

  get statusUpdateLabel() {
    const { contributorsRegistry, status } = this;
    if (status === 'DEACTIVATED') {
      return {
        label: 'deactivatedWhenBy',
        date: this.deactivatedAt,
        user: this.deactivatedBy ? contributorsRegistry[this.deactivatedBy]?.login : 'VOLTA'
      };
    } else if (status === 'EDITED' || status === 'READY_FOR_REVIEW') {
      return {
        label: 'updatedWhenBy',
        date: this.updatedAt,
        user: this.updatedBy ? contributorsRegistry[this.updatedBy]?.login : 'VOLTA'
      };
    } else if (status === 'VALIDATED') {
      return {
        label: 'validatedWhenBy',
        date: this.validatedAt,
        user: this.validatedBy ? contributorsRegistry[this.validatedBy]?.login : 'VOLTA'
      };
    }

    return {
      label: 'createdWhenBy',
      date: this.createdAt,
      user: contributorsRegistry[this.createdBy]?.login
    };
  }

  updateCompletion(completion: number) {
    if (completion !== this.validationCompletion) {
      this.validationCompletion = completion;

      if (completion === 1) {
        this.status = 'VALIDATED';
      }

      if (completion < 1 && this.status === 'READY_FOR_REVIEW') {
        this.status = 'EDITED';
      }
    }
  }

  canUserDo = (action: string) => {
    const permission =
      `ForecastProjects${action.replace('ForecastProjects', '')}` as TForecastEntityPermission;
    return (
      this.sessionUser.user.isStaff ||
      this.permissions[this.sessionUser.userId]?.some(
        (p) => p === 'All' || p === 'ForecastProjectsAll' || p === permission
      )
    );
  };

  [Type] = 'forecast-project' as const;
}

export const ForecastQuantiles = {
  P10: 0.1,
  P50: 0.5,
  P90: 0.9
} as const;

export type TForecastQuantile = keyof typeof ForecastQuantiles;

export type TForecastEntityPermission =
  | 'All'
  | 'ForecastProjectsAll'
  | 'ForecastProjectsCopyForecastToFinal'
  | 'ForecastProjectsUpdateForecastGroup'
  | 'ForecastProjectsValidateForecastGroup'
  | 'ForecastProjectsValidateProject';

export type TForecastProjectStatus =
  | 'INITIALISED'
  | 'EDITED'
  | 'DEACTIVATED'
  | 'VALIDATED'
  | 'READY_FOR_REVIEW';

export type TDatasetAttributeType =
  | 'STRING'
  | 'INTEGER'
  | 'FLOAT'
  | 'TIMESTAMP'
  | 'GEOLOCATION'
  | 'UNKNOWN';

export type TSkuProperty = {
  property: string;
  propertyType: TDatasetAttributeType;
};

export type TRTSProperty = {
  property: string;
  propertyType: TDatasetAttributeType;
};

export type TForecastProjectStats = {
  skuCount: number;
  sourceCount: number;
  excludedSkuCount: number;
};

export const ForecastProjectStatusColors: Record<
  TForecastProjectStatus,
  { color?: THueVariationString; icon?: string; label: string }
> = {
  INITIALISED: { color: 'gray', icon: 'sparkles', label: 'forecastProjectStatuses.INITIALISED' },
  EDITED: { color: 'purple', icon: 'edit', label: 'forecastProjectStatuses.EDITED' },
  DEACTIVATED: { color: 'red', icon: 'close', label: 'forecastProjectStatuses.DEACTIVATED' },
  VALIDATED: { color: 'green', icon: 'checkmark', label: 'forecastProjectStatuses.VALIDATED' },
  READY_FOR_REVIEW: {
    color: 'orange',
    icon: 'eye',
    label: 'forecastProjectStatuses.READY_FOR_REVIEW'
  }
} as const;

export const ForecastFrequencyOptions: Record<TForecastFrequency, TFrequencyOption> = indexByFn(
  Object.values(FrequencyOptions),
  (o) => o.forecast
);

export const GroupOptions: Record<
  TExternalForecastField,
  {
    color?: THueVariationString;
    label?: string;
    valueLabel?: (f: TForecastGroupField) => string;
    excludeFromSelection?: boolean;
  }
> = {
  sku: { color: 'blue' },
  part: { color: 'blue', label: 'partCode' },
  warehouse: { color: 'green' },
  supplier: { color: 'primary' },
  workshop: { color: 'orange' },
  deliveryWorkshop: { color: 'purple' },
  acqCode: { valueLabel: (f) => `acqCodes.${f.value}` },
  methodology: { valueLabel: (f) => `planningMethodologies.${f.value}` },
  partType: { color: 'indigo' },
  partUnitMeasure: { valueLabel: (f) => `unitMeasures.${f.value}` },
  reorderStrategy: { valueLabel: (f) => `reorderStrategies.${f.value}` },
  buyer: {},
  orderType: {},
  planner: {},
  source: {},
  'sku.abcClass': { label: 'abcClass' },
  'sku.xyzClass': { label: 'xyzClass' },
  'sku.leadTimeCategory': {
    label: 'leadTimeCategory',
    valueLabel: (f) => `leadTimeCategories.${f.value}`
  },
  'sku.varCategory': { label: 'varCategory', valueLabel: (f) => `varCategories.${f.value}` },
  'period.day': { excludeFromSelection: true },
  'period.week': { excludeFromSelection: true },
  'period.month': { excludeFromSelection: true },
  'period.quarter': { excludeFromSelection: true },
  'period.year': { excludeFromSelection: true }
};

export interface ICreateForecastProjectCmd {
  automlEnabled?: boolean;
  cron?: string;
  dataGroupArn?: string;
  defaultGroupBy?: TExternalForecastField[][];
  description?: string;
  forecastFrequency: TForecastFrequency;
  forecastHorizon: number;
  manualForecastTypes?: string[];
  metadataModel: TSkuProperty[];
  name: string;
  permissions: Record<string, TForecastEntityPermission[]>;
  rtsModel: TRTSProperty[];
  scheduleId?: string;
  selectedForecastQuantiles?: TForecastQuantile[];
  selectedManualSources?: string[];
  skuSelection: IJsonApiQuery['filter'];
  timezone?: string;
}

export interface IUpdateForecastProjectCmd {
  automlEnabled?: boolean;
  cron?: string;
  dataGroupArn?: string;
  defaultGroupBy?: TExternalForecastField[][];
  description?: string;
  forecastFrequency?: TForecastFrequency;
  forecastHorizon?: number;
  manualForecastTypes?: string[];
  metadataModel?: TSkuProperty[];
  name?: string;
  permissions?: Record<string, TForecastEntityPermission[]>;
  rtsModel?: TRTSProperty[];
  selectedForecastQuantiles?: TForecastQuantile[];
  selectedManualSources?: string[];
  skuSelection?: IJsonApiQuery['filter'];
  timezone?: string;
}

export interface ICreateForecastProjectRevisionCmd {
  revision: string;
}
